import React from 'react';
import { cn } from '@/lib/utils';
import { ActivityItem } from './ActivityItem';
import { DashboardMetrics } from '../utils/dashboardTypes';
import { Briefcase, Users, UserCheck, ChevronRight } from 'lucide-react';

interface ActivitySectionProps {
  metrics: DashboardMetrics;
  onNavigate: (path: string) => void;
  horizontal?: boolean;
  limit?: number;
  compact?: boolean;
}

export function ActivitySection({ metrics, onNavigate, horizontal = false, limit = 3, compact = false }: ActivitySectionProps) {
  const { recentRoles, recentCandidates, recentMatches } = metrics;

  // Limit items for display
  const displayedRoles = horizontal ? recentRoles.slice(0, limit) : recentRoles;
  const displayedCandidates = horizontal ? recentCandidates.slice(0, limit) : recentCandidates;
  const displayedMatches = horizontal ? recentMatches.slice(0, limit) : recentMatches;

  // Column header component
  const ColumnHeader = ({
    icon: Icon,
    title,
    count
  }: {
    icon: React.ElementType;
    title: string;
    count: number;
  }) => (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2.5">
        <div className={cn(
          "w-8 h-8 rounded-lg",
          "flex items-center justify-center",
          "bg-slate-100 dark:bg-slate-800"
        )}>
          <Icon className="w-4 h-4 text-teal-600 dark:text-teal-400" />
        </div>
        <h3 className="font-serif text-lg font-semibold text-slate-800 dark:text-slate-100">
          {title}
        </h3>
      </div>
      {count > 0 && (
        <span className={cn(
          "px-2 py-0.5 rounded-full",
          "text-xs font-medium",
          "bg-slate-100 dark:bg-slate-700",
          "text-slate-600 dark:text-slate-400"
        )}>
          {count}
        </span>
      )}
    </div>
  );

  // View all link component
  const ViewAllLink = ({ onClick, label }: { onClick: () => void; label: string }) => (
    <button
      onClick={onClick}
      className={cn(
        "group flex items-center justify-center gap-1.5",
        "w-full mt-4 py-2.5 rounded-lg",
        "text-sm font-medium",
        "text-teal-600 dark:text-teal-400",
        "bg-teal-50/50 dark:bg-teal-950/30",
        "hover:bg-teal-100 dark:hover:bg-teal-950/50",
        "border border-transparent",
        "hover:border-teal-200 dark:hover:border-teal-800",
        "transition-all duration-200"
      )}
    >
      {label}
      <ChevronRight className={cn(
        "w-4 h-4",
        "group-hover:translate-x-0.5",
        "transition-transform duration-200"
      )} />
    </button>
  );

  // Empty state component
  const EmptyState = ({ message }: { message: string }) => (
    <div className={cn(
      "flex flex-col items-center justify-center",
      "py-8 px-4 rounded-xl",
      "bg-slate-50/50 dark:bg-slate-800/30",
      "border border-dashed border-slate-200 dark:border-slate-700"
    )}>
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {message}
      </p>
    </div>
  );

  return (
    <div className={cn(
      "grid gap-6",
      compact
        ? "grid-cols-1 sm:grid-cols-2"
        : "grid-cols-1 lg:grid-cols-2 xl:grid-cols-3"
    )}>
      {/* Recent Roles */}
      <div className={cn(
        "flex flex-col",
        compact && "sm:col-span-1"
      )}>
        <ColumnHeader
          icon={Briefcase}
          title="Recent Roles"
          count={displayedRoles.length}
        />
        {displayedRoles.length > 0 ? (
          <>
            <div className="space-y-3 flex-1">
              {displayedRoles.map((role: any) => (
                <ActivityItem
                  key={role.id}
                  type="role"
                  title={role.title}
                  subtitle={role.location?.displayName || 'Remote'}
                  timestamp={role.createdAt}
                  onClick={() => onNavigate(`/my-roles`)}
                />
              ))}
            </div>
            <ViewAllLink
              onClick={() => onNavigate('/my-roles')}
              label="View All Roles"
            />
          </>
        ) : (
          <EmptyState message="No recent roles" />
        )}
      </div>

      {/* Recent Candidates */}
      <div className={cn(
        "flex flex-col",
        compact && "sm:col-span-1"
      )}>
        <ColumnHeader
          icon={Users}
          title="Recent Candidates"
          count={displayedCandidates.length}
        />
        {displayedCandidates.length > 0 ? (
          <>
            <div className="space-y-3 flex-1">
              {displayedCandidates.map((candidate: any) => (
                <ActivityItem
                  key={candidate.id}
                  type="candidate"
                  title={`${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 'Candidate'}
                  subtitle={candidate.jobTitle || 'No title'}
                  timestamp={candidate.createdAt || candidate.updatedAt}
                  onClick={() => onNavigate(`/my-candidates`)}
                />
              ))}
            </div>
            <ViewAllLink
              onClick={() => onNavigate('/my-candidates')}
              label="View All Candidates"
            />
          </>
        ) : (
          <EmptyState message="No recent candidates" />
        )}
      </div>

      {/* Recent Matches - hidden in compact mode */}
      {!compact && (
        <div className="flex flex-col lg:col-span-2 xl:col-span-1">
          <ColumnHeader
            icon={UserCheck}
            title="Recent Matches"
            count={displayedMatches.length}
          />
          {displayedMatches.length > 0 ? (
            <>
              <div className="space-y-3 flex-1">
                {displayedMatches.map((match: any) => (
                  <ActivityItem
                    key={match.id}
                    type="match"
                    title={match.jobTitle || 'Match'}
                    subtitle={match.candidateName || match.candidateJobTitle || 'Candidate'}
                    timestamp={match.createdAt}
                    onClick={() => onNavigate(`/matches`)}
                  />
                ))}
              </div>
              <ViewAllLink
                onClick={() => onNavigate('/matches')}
                label="View All Matches"
              />
            </>
          ) : (
            <EmptyState message="No recent matches" />
          )}
        </div>
      )}
    </div>
  );
}
